import { Patch } from 'immer';
import clonedeep from 'lodash.clonedeep';
import { UnsavedPatch } from 'modules/Editor/store/history/history.types';
import { initialState } from 'modules/Editor/store/infographSlice';
import { DesignGenerationType } from 'types/ai.types';
import { InfographId, PageId, WidgetId } from 'types/idTypes';
import { InfographState, PagesMap, WidgetsMap } from 'types/infographTypes';
import { Page } from 'types/pageTypes';
import { AllWidgetData } from 'widgets/Widget.types';

import { getPageWidgetIds } from '../../widgets/Widget.helpers';
import { APIBackend } from './APIBackend';
import ChecksumManager from './ChecksumManager';
import {
  checkIsDeleteOp,
  combineUnsavedPatchesToMap,
  parseApplyPatches,
  parseChecksum,
  parseChecksumList,
  parsePathToMap,
} from './Client.helpers';
import * as types from './types';

/**
 * e2-api client to access the api.
 */
export class Client {
  readonly api: APIBackend;

  constructor(backend: APIBackend) {
    this.api = backend;
  }

  /**
   * Save entire InfographState to backend.
   *
   * Ues /commit endpoint, breaks down each infograph, each page, each widget, into
   * write command for /commit endpoint.
   *
   * @param infographSliceState
   */
  async saveFullInfograph(infographSliceState: InfographState) {
    // split to infograph, page, widget documents
    const { id: infographId, pages, widgets } = infographSliceState;

    // If these essential ones are not in object, do nothing
    if (!infographId) throw new Error('Invalid Infograph State');

    // Manage all command list
    const commitCommandInfos: types.CommitCommandsInfo[] = [];

    // Save infograph, except pages and widgets
    const copyInfograph: Partial<InfographState> = clonedeep(infographSliceState);
    delete copyInfograph.pages;
    delete copyInfograph.widgets;

    // Set the checksum data
    const checksumManager = ChecksumManager.getInstance();
    const checksum = checksumManager.get();

    commitCommandInfos.push({
      id: infographId,
      type: 'infographs',
      command: {
        set: {
          path: `infographs/${infographId}`,
          fields: copyInfograph,
          checksum: checksum?.infographs[infographId],
        },
      },
    });

    // Save pages
    for (const pageId of Object.keys(pages)) {
      commitCommandInfos.push({
        id: pageId,
        type: 'pages',
        command: {
          set: {
            path: `infographs/${infographId}/pages/${pageId}`,
            fields: pages[pageId],
            checksum: checksum?.pages[pageId],
          },
        },
      });

      const pageWidgetIds = getPageWidgetIds(pages[pageId], widgets);

      // Save widgets
      for (const widgetId of pageWidgetIds) {
        if (!widgets[widgetId]) continue;

        commitCommandInfos.push({
          id: widgetId,
          type: 'widgets',
          command: {
            set: {
              path: `infographs/${infographId}/pages/${pageId}/widgets/${widgetId}`,
              fields: widgets[widgetId],
              checksum: checksum?.widgets[widgetId],
            },
          },
        });
      }
    }

    const commitCommands = commitCommandInfos.map((data) => data.command);

    const { writeResults } = await this.api.request<types.CommitResponse>('/commit', {
      writes: commitCommands,
    });

    // Check if contains null
    const hasError = writeResults.some((data) => data === null);
    if (hasError) throw new Error('Bad request');

    // Update checksum
    if (checksumManager.get()) {
      writeResults.forEach((data, index) => {
        const { id, type } = commitCommandInfos[index];
        const newChecksum = data?.checksum;
        if (!newChecksum) return;
        checksumManager.patch(type, id, newChecksum);
      });
    }
  }

  /**
   * It trigger commit request using the unsaved patches in the history store
   * If checksum exist, it uses the patch command, otherwise uses the set command
   *
   * @param infographSliceState
   * @param unsavedPatches
   */
  async commitUnsavedPatches(infographSliceState: InfographState, unsavedPatches: UnsavedPatch[]) {
    // Manage all command list
    let commitCommandInfos: types.CommitCommandsInfo[] = [];

    // Set the checksum data
    const checksumManager = ChecksumManager.getInstance();
    const checksum = checksumManager.get();

    const unsavedPatchesMap = combineUnsavedPatchesToMap(unsavedPatches);

    // Generate the commitCommandInfos
    Object.keys(unsavedPatchesMap).forEach((path) => {
      const { type: targetType, id: targetId } = parsePathToMap(path);
      const targetChecksum = checksum?.[targetType][targetId];
      const usePatch = !!targetChecksum;
      let targetFields;

      if (targetType === 'infographs') {
        // Save infograph, except pages and widgets
        const copyInfograph: Partial<InfographState> = clonedeep(infographSliceState);
        delete copyInfograph.pages;
        delete copyInfograph.widgets;
        targetFields = copyInfograph;
      } else if (targetType === 'pages') {
        targetFields = infographSliceState.pages[targetId];
      } else if (targetType === 'widgets') {
        targetFields = infographSliceState.widgets[targetId];
      }

      if (usePatch) {
        // If checksum exists, use the patch command.
        const targetPatches = parseApplyPatches(unsavedPatchesMap[path].patches, targetType);

        // Only delete if delete op is detected and targetFields is empty (i.e. removed from infograph state)
        const isDeleteOp = checkIsDeleteOp(targetPatches) && !targetFields;

        if (isDeleteOp) {
          commitCommandInfos.push({
            id: targetId,
            type: targetType,
            command: {
              delete: {
                path: path,
                checksum: targetChecksum,
              },
            },
          });
        } else {
          commitCommandInfos.push({
            id: targetId,
            type: targetType,
            command: {
              patch: {
                path: path,
                patches: targetPatches,
                checksum: targetChecksum,
              },
            },
          });
        }
      } else {
        // If the checksum does not exist, use the set command on the page and widget data
        // If the targetFields is empty, it means the target is not exists in the infographSliceState.
        // For example, User quickly copy or add a new widget and delete or undo it before trigger the commit request.
        // In this case, we don't need to commit that patch.
        const firstPatch = unsavedPatchesMap[path]?.patches?.[0];
        if (!targetFields) {
          // If the widget wasn't first added but targetFields is empty, it means it was a previously added widget that was deleted
          // For example, a widget that was upgraded by the version manager was deleted before being saved to the DB
          if (firstPatch?.op !== 'add') {
            commitCommandInfos.push({
              id: targetId,
              type: targetType,
              command: {
                delete: {
                  path: path,
                },
              },
            });
          }
          return;
        }

        commitCommandInfos.push({
          id: targetId,
          type: targetType,
          command: {
            set: {
              path: path,
              fields: targetFields,
            },
          },
        });
      }
    });

    const commitCommands = commitCommandInfos.map((data) => data.command);

    const { writeResults } = await this.api.request<types.CommitResponse>('/commit', {
      writes: commitCommands,
    });

    // Check if contains null
    const hasError = writeResults.some((data) => data === null);
    if (hasError) throw new Error('Bad request');

    // Update checksum
    if (checksumManager.get()) {
      writeResults.forEach((data, index) => {
        const { id, type } = commitCommandInfos[index];
        const newChecksum = data?.checksum;
        if (!newChecksum) return;
        checksumManager.patch(type, id, newChecksum);
      });
    }
  }

  /**
   * Fetch all infograph/page/widget document that belongs to an infograph.
   *
   * @param infographId
   */
  async getFullInfograph(
    infographId: InfographId,
  ): Promise<{ infograph: InfographState; widgetPageMap: { [widgetId: WidgetId]: PageId } }> {
    // Paths for all infograph/page/widget documents
    const docPaths = [
      `infographs/${infographId}`,
      `infographs/${infographId}/pages`,
      `infographs/${infographId}/widgets`,
    ];

    // Fetch all documents
    const responseObj = await this.api.request<types.BatchGetResponse>('/batchGet', { paths: docPaths });

    const docs = responseObj.docs;
    if (!docs) throw new Error('Expected docs property from response but not found');

    /**
     * Initialize ChecksumMap
     */
    const newChecksumMap = ChecksumManager.DEFAULT_DATA;
    let fullInfograph: InfographState; // Parse infograph data

    if (!docs[0] || Array.isArray(docs[0])) throw new Error('Infograph not found');

    // Set Infograph
    fullInfograph = parseInfograph(docs[0] as types.InfographRow);
    fullInfograph.pages = {}; // init pages
    fullInfograph.widgets = {}; // init widgets

    const widgetPageMap: { [widgetId: WidgetId]: PageId } = {};

    // Set checksum for infograph data
    newChecksumMap.infographs = parseChecksum(docs[0]);

    // Parse list of pages
    if (docs[1] && Array.isArray(docs[1])) {
      fullInfograph.pages = parsePages(docs[1] as types.PageRow[]);
      newChecksumMap.pages = parseChecksumList(docs[1]);
    }

    // Parse all widget rows to correct type for redux state
    if (docs[2] && Array.isArray(docs[2])) {
      fullInfograph.widgets = parseWidgets(docs[2] as types.WidgetRow[], widgetPageMap);
      newChecksumMap.widgets = parseChecksumList(docs[2]);
    }

    // Set the checksum data when it gets full infograph data
    const checksumManager = ChecksumManager.getInstance();
    checksumManager.set(newChecksumMap);

    return { infograph: fullInfograph, widgetPageMap };
  }

  /**
   * Get list of recently modified infographs. The /query endpoint returns last 100 infographs.
   *
   * TODO: This endpoint will fail if proper permission is not contained in JWT token (in future)
   */
  async getAllInfographs() {
    // TODO: Handle error
    const r = await this.api.request<types.QueryResponse>('/query', {});
    return r['queryResult'];
  }

  /**
   * Create a new infograph from a template.
   *
   * Will create new infograph by copying source infograph pointed by the template.
   *
   * You can choose to patch the source infograph with any data. The patch will be applied to
   * a clone of source infograph, so the paths in the patch must be valid for source infograph
   * of the template.
   *
   * @param templateId Target template ID
   * @param patches Immer patch to be applied before cloned
   */
  async newInfographFromTemplate(
    templateId: string,
    patches: Patch[] = [],
  ): Promise<types.NewInfographFromTemplateResponse> {
    // TODO: Handle error
    return await this.api.request<types.NewInfographFromTemplateResponse>('/templates/newInfograph', {
      fromTemplateId: templateId,
      patches,
    });
  }

  /**
   * Duplicate any infograph.
   *
   * Given an infograph id, create an identical infograph with new ids for infograph, all pages,
   * and all widgets that belong to the infograph.
   *
   * @param infographId
   */
  async duplicateInfograph(infographId: string): Promise<types.DuplicateResponse> {
    const r = await this.api.request<types.DuplicateResponse>('/duplicate', {
      sourcePath: `infographs/${infographId}`,
    });

    // TODO: Handle error
    return r;
  }

  /**
   * Create new template from infograph.
   *
   * Creating a new template will result in duplicating the source infograph. Duplicated source
   * infograph will be tagged as a template infograph, which will limit what can be done
   * to the infograph. (ex. not implemented yet, but will prevent it from being deleted)
   *
   * Create new entry in templates table.
   *
   * @param infographId
   */
  async newTemplate(infographId: string): Promise<types.NewTemplateResponse> {
    const r = await this.api.request<types.NewTemplateResponse>('/templates/create', {
      sourceInfographId: infographId,
    });

    // TODO: Handle error
    return r;
  }

  async getAllTemplates(templateId?: string): Promise<types.TemplateResponseBody['templates']> {
    const r = await this.api.request<types.TemplateResponseBody>('/templates/query', { templateId });

    // TODO: Handle error
    return r['templates'];
  }

  /**
   * Update design by prompt
   *
   * Given an infograph id, create an identical infograph with new ids for infograph, all pages,
   * and all widgets that belong to the infograph.
   *
   * @param designId - design id to update
   * @param prompt - prompt to update design by
   */
  async updateDesignByPrompt(
    designId: string,
    prompt: string,
    saveToDB = true,
  ): Promise<types.UpdateDesignByPromptResponseBody> {
    const r = await this.api.request<types.UpdateDesignByPromptResponseBody>('/updateDesignByPrompt', {
      designId,
      prompt,
      saveToDB,
    });

    // TODO: Handle error
    return r;
  }

  async getAllowedDesignTags({ type }: { type: DesignGenerationType }) {
    return await this.api.request<types.GetAllowedTagResponseBody>('/getAllowedDesignTags', { type });
  }

  /**
   * Generate text from sample text and instructions using OpenAI API.
   *
   * @param sampleText - Text to apply instructions to
   * @param instructions - Instructions to apply
   * @returns Streaming response
   */
  async generateText(sampleText: string, instructions: string, userId?: string) {
    return this.api.streamRequest('/generateText', {
      sampleText,
      instructions,
      userId,
    });
  }

  /**
   * Generate image caption from image url.
   *
   * @param url
   * @returns
   */
  async generateImageCaption(url: string) {
    return await this.api.request<types.GenerateAltTextResponseBody>('/generateCaption', {
      url,
    });
  }

  async generateChartAltText(params: types.GenerateChartAltTextProps) {
    return await this.api.request<types.GenerateAltTextResponseBody>('/generateChartAltText', params);
  }

  async generateMapAltText(params: types.GenerateMapAltTextProps) {
    return await this.api.request<types.GenerateAltTextResponseBody>('/generateMapAltText', params);
  }

  async detectPhishing(params: types.DetectPhishingProps) {
    return await this.api.request<types.PhishingResponseBody>('/detectPhishing', params);
  }

  async removeBackgroundImage(url: string) {
    const response = await this.api.request<{ image: string }>('/removeBackground', { url });
    return response?.image || '';
  }

  async generateStandaloneImage(params: types.GenerateStandaloneImageProps) {
    const response = await this.api.request<types.GenerateStandaloneImageResponseBody>(
      '/generateStandaloneImage',
      params,
    );

    return response;
  }
}

/**
 * Returns a valid InfographState from /batchGet response, filling in all required data (supplied
 * by `initialState`).
 *
 * @param infographRow
 */
function parseInfograph(infographRow: types.InfographRow): InfographState {
  if (infographRow['id'] && infographRow['infograph']) {
    // Use initial state + override any data got back from server
    return {
      ...initialState,
      ...infographRow['infograph'],
      id: infographRow['id'].trim() as InfographId,
    };
  } else {
    throw new Error('Could not find proper infograph id and data from response');
  }
}

/**
 * Creates valid PageMap from rows of Page documents returned by `/batchGet` endpoint.
 *
 * @param pageRows
 */
function parsePages(pageRows: types.PageRow[]): PagesMap {
  return pageRows.reduce<PagesMap>((prev, curr: types.PageRow) => {
    if (curr['id'] && curr['page']) {
      prev[(curr['id'] as PageId).trim()] = curr['page'] as Page;
    } else {
      throw new Error('Could not find proper page id and data from response');
    }
    return prev;
  }, {});
}

/**
 * Creates valid WidgetsMap from rows of Widget documents returned by `/batchGet` endpoint.
 *
 * @param widgetRows
 */
function parseWidgets(widgetRows: types.WidgetRow[], widgetPageMap?: { [widgetId: WidgetId]: PageId }): WidgetsMap {
  return widgetRows.reduce<WidgetsMap>((prev, curr: types.WidgetRow) => {
    if (curr['id'] && curr['widget']) {
      prev[(curr['id'] as WidgetId).trim()] = curr['widget'] as AllWidgetData;

      // assign the page_id to the widgetPageMap if provided
      if (widgetPageMap && curr['page_id']) {
        widgetPageMap[(curr['id'] as WidgetId).trim()] = curr['page_id'] as PageId;
      }
    } else {
      throw new Error('Could not find proper widget id and data from response');
    }
    return prev;
  }, {});
}
